import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import Switch from '../../ui/switch';
import Label from '../../ui/label';
import Separator from '../../ui/separator';
import { Alert, AlertDescription } from '../../ui/alert';
import useActivityFeed from '../../../hooks/useActivityFeed';
import ActivityItem from './ActivityItem';

// Wrapper component to create a proper label-switch association
const LabeledSwitch: React.FC<{
  id: string;
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}> = ({ id, label, checked, onCheckedChange }) => (
  <div className='flex items-center space-x-2'>
    <div
      className='flex items-center space-x-2 cursor-pointer'
      onClick={() => onCheckedChange(!checked)}>
      <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} />
      <Label htmlFor={id} className='cursor-pointer'>
        {label}
      </Label>
    </div>
  </div>
);

const ActivityFeed: React.FC = () => {
  const { activities, loading, error, filterOptions, setFilterOptions } = useActivityFeed();

  const handleIncludeSelfChange = (checked: boolean) => {
    setFilterOptions({ ...filterOptions, includeSelf: checked });
  };

  const handleIncludeFollowingChange = (checked: boolean) => {
    setFilterOptions({ ...filterOptions, includeFollowing: checked });
  };

  const renderLoadingState = () => (
    <div className='flex justify-center p-8'>
      <div className='h-8 w-8 animate-spin rounded-full border-b-2 border-primary'></div>
    </div>
  );

  const renderNoActivityState = () => {
    let message = 'No activity found. Start by asking a question or following other users.';

    if (!filterOptions.includeSelf && !filterOptions.includeFollowing) {
      message = "You've disabled all activity sources. Enable one to see activity.";
    } else if (filterOptions.includeSelf && !filterOptions.includeFollowing) {
      message = "You haven't posted any questions or answers yet.";
    } else if (!filterOptions.includeSelf && filterOptions.includeFollowing) {
      message = "Users you follow haven't posted any questions or answers yet.";
    }

    return (
      <div className='text-center py-12'>
        <h3 className='text-lg font-medium mb-2'>No Activity Found</h3>
        <p className='text-muted-foreground mb-4'>{message}</p>
      </div>
    );
  };

  const renderActivityFeed = () => (
    <Tabs defaultValue='all' className='w-full'>
      <TabsList className='grid w-full grid-cols-3'>
        <TabsTrigger value='all'>All Activity</TabsTrigger>
        <TabsTrigger value='questions'>Questions</TabsTrigger>
        <TabsTrigger value='answers'>Answers</TabsTrigger>
      </TabsList>

      <TabsContent value='all' className='mt-4'>
        <div className='space-y-4'>
          {activities.map((activity, index) => (
            <React.Fragment key={`${activity.type}-${index}`}>
              <ActivityItem activity={activity} />
              {index < activities.length - 1 && <Separator />}
            </React.Fragment>
          ))}
        </div>
      </TabsContent>

      <TabsContent value='questions' className='mt-4'>
        <div className='space-y-4'>
          {activities
            .filter(activity => activity.type === 'question')
            .map((activity, index, filtered) => (
              <React.Fragment key={`question-${index}`}>
                <ActivityItem activity={activity} />
                {index < filtered.length - 1 && <Separator />}
              </React.Fragment>
            ))}

          {activities.filter(activity => activity.type === 'question').length === 0 && (
            <p className='text-center text-muted-foreground py-8'>
              No questions found in the activity feed.
            </p>
          )}
        </div>
      </TabsContent>

      <TabsContent value='answers' className='mt-4'>
        <div className='space-y-4'>
          {activities
            .filter(activity => activity.type === 'answer')
            .map((activity, index, filtered) => (
              <React.Fragment key={`answer-${index}`}>
                <ActivityItem activity={activity} />
                {index < filtered.length - 1 && <Separator />}
              </React.Fragment>
            ))}

          {activities.filter(activity => activity.type === 'answer').length === 0 && (
            <p className='text-center text-muted-foreground py-8'>
              No answers found in the activity feed.
            </p>
          )}
        </div>
      </TabsContent>
    </Tabs>
  );

  // Determine content to render based on state
  let content;
  if (loading) {
    content = renderLoadingState();
  } else if (activities.length > 0) {
    content = renderActivityFeed();
  } else {
    content = renderNoActivityState();
  }

  return (
    <div className='container py-8'>
      <Card>
        <CardHeader>
          <CardTitle className='text-2xl'>Activity Feed</CardTitle>
          <div className='flex flex-col gap-4 mt-4 sm:flex-row sm:items-center sm:justify-between'>
            <LabeledSwitch
              id='include-self'
              label='Show my activity'
              checked={!!filterOptions.includeSelf}
              onCheckedChange={handleIncludeSelfChange}
            />
            <LabeledSwitch
              id='include-following'
              label='Show activity from users I follow'
              checked={!!filterOptions.includeFollowing}
              onCheckedChange={handleIncludeFollowingChange}
            />
          </div>
        </CardHeader>

        <CardContent>
          {error && (
            <Alert variant='destructive' className='mb-4'>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {content}
        </CardContent>
      </Card>
    </div>
  );
};

export default ActivityFeed;
